import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"

import { selectSpawnArrivals, spawnTourEnded, spawnTourStarted } from "src/store/ui"
import { raceNames } from "src/utils/races"
import { withSpawnTarget } from "src/utils/roundEventWording"
import { playUiSound } from "src/utils/audio"

import "./RoundFlash.css"
import "./SpawnTour.css"

/* "So players are aware" (the owner's ask, 2026-09-16): a survival wave lands somewhere on a
   200x100 world, and a line in the dock is not how somebody finds out that four bots just took
   up residence eleven rows behind them. So the map goes to each newcomer in turn — a flight, a
   step of zoom, a banner naming it, and on to the next — and then back to where the player was.

   Three things it is, and each is a rule the rest of this app already keeps:

   - **It is cosmetics.** `SPAWN_TOUR_DWELL_MS` is a client constant and nothing here is a claim
     about what the server will do; every fact on the banner is read off the payload's own seat.
   - **It is skippable, and getting out is one press.** A fourth wave of two bots is eight seconds
     of a round somebody may be fighting through. Click, Esc or Space moves on to the next
     newcomer; press again and the tour is over and the camera goes home.
   - **It does not run in a replay.** `active` is the view's own `!reviewing` — read from the
     Game view rather than from `selectIsReviewing`, which is true for a live spectator too, and a
     spectator is exactly who this is for. A stepper scrubbing sixty ticks would be interrupted on
     every wave tick; the ring and the report line still show there.

   The camera is the Game view's: `getCamera` reads where the player was (the `onMoveHooks`'
     second argument) and `onFlyTo` is the `flyTo` prop's setter. Nothing here touches the canvas,
     so there is still one camera. */

// Long enough to read three words and see where on the map they are, short enough that a
// four-bot wave is not a minute of somebody else's cinema. The same beat `RoundFlash` holds for.
export const SPAWN_TOUR_DWELL_MS = 1800
// One step of the map's own zoom, so the tour lands at a size the +/- pair could have reached.
export const SPAWN_TOUR_ZOOM_STEP = 8

const SpawnTour = ({ active = true, getCamera = null, onFlyTo = null }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const arrivals = useSelector(selectSpawnArrivals)
  /* The tour being walked: the newcomers in order, which one is on screen, and the camera to put
     back afterwards. In component state rather than the store because it is this view's business
     and dies with it; what the *store* holds is only the two facts anything else needs — which
     arrivals have been toured, and whether one is running. */
  const [tour, setTour] = useState(null)
  const flightSeq = useRef(0)
  // The newcomer the last press landed on. It is how "a second press ends it" is told from "the
  // dwell ran out and the next press is a fresh skip" — the timer clears it, a press sets it.
  const skippedTo = useRef(null)

  const fly = useCallback(
    (camera) => {
      flightSeq.current += 1
      onFlyTo?.({ ...camera, seq: flightSeq.current })
    },
    [onFlyTo]
  )

  // A wave has landed. Marked as toured before anything moves, so a payload re-reduced mid-tour
  // cannot queue the same bot twice; appended when one lands while a tour is already running,
  // which is the only way two waves can overlap.
  useEffect(() => {
    if (!active || arrivals.length === 0) return
    dispatch(spawnTourStarted(arrivals.map((arrival) => arrival.key)))
    setTour((current) =>
      current
        ? { ...current, list: [...current.list, ...arrivals] }
        : { list: arrivals, index: 0, camera: getCamera?.() || null }
    )
    // `getCamera` is read once, at the start: where the player was standing when the wave landed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, arrivals, dispatch])

  // The view stopped being a live game under it — it finished, or it flipped into review. Put the
  // banner away and let go of the digest's gate rather than leaving a tour nobody can end.
  useEffect(() => {
    if (active || !tour) return
    setTour(null)
    dispatch(spawnTourEnded())
  }, [active, tour, dispatch])

  // One leg: fly, sound, dwell, next. The last leg flies home and hands the round digest its turn.
  useEffect(() => {
    if (!tour) return undefined
    const arrival = tour.list[tour.index]
    if (!arrival) {
      if (tour.camera) fly(tour.camera)
      setTour(null)
      dispatch(spawnTourEnded())
      return undefined
    }
    const camera = tour.camera
    fly({
      // A cell is centred by its own column and row plus a half — `flyTo` takes world points.
      col: arrival.column + 0.5,
      row: arrival.row + 0.5,
      squareSize: (camera?.squareSize ?? 24) + SPAWN_TOUR_ZOOM_STEP,
    })
    // A thing that happened to the player, which is what the catalogue's sounds are for. The
    // battle sweep rather than a chime: an enemy arriving is an alarm, not a confirmation.
    playUiSound("battle")
    const timer = setTimeout(() => {
      skippedTo.current = null
      setTour((current) => (current ? { ...current, index: current.index + 1 } : current))
    }, SPAWN_TOUR_DWELL_MS)
    return () => clearTimeout(timer)
  }, [tour, dispatch, fly])

  const skip = useCallback(() => {
    setTour((current) => {
      if (!current) return current
      // Pressed again on the newcomer the last press brought us to: they have seen enough.
      const ending = skippedTo.current === current.index
      skippedTo.current = ending ? null : current.index + 1
      return { ...current, index: ending ? current.list.length : current.index + 1 }
    })
  }, [])

  useEffect(() => {
    if (!tour) return undefined
    const onKeyDown = (event) => {
      if (event.key !== "Escape" && event.key !== " " && event.key !== "Spacebar") return
      // The map's own Escape backs out of a march being planned; while the tour holds the camera
      // it holds the key, and the press is not passed on to anything underneath.
      event.preventDefault()
      event.stopPropagation()
      skip()
    }
    // Capture, so this runs before the Game view's own Escape handler rather than after it.
    window.addEventListener("keydown", onKeyDown, true)
    return () => window.removeEventListener("keydown", onKeyDown, true)
  }, [tour, skip])

  if (!tour) return null
  const arrival = tour.list[tour.index]
  if (!arrival) return null

  const line = withSpawnTarget(
    t(arrival.wave > 0 ? "game.spawnTour.line" : "game.spawnTour.lineOpening", {
      name: arrival.name ?? "?",
      race: raceNames[arrival.race] || arrival.race || "?",
      difficulty: arrival.difficulty
        ? t(`game.forces.difficulty_${arrival.difficulty}`, { defaultValue: arrival.difficulty })
        : "?",
      wave: arrival.wave,
    }),
    // The same sentence the report line uses, so the banner and the dock say it the same way.
    arrival.target
  )

  return (
    <>
      {/* The press, caught over the board and nowhere else. The banner itself keeps the
          `pointer-events: none` every caption over this map has — an order swallowed by a label
          costs more than the label is worth — so the skip is this sheet, and it covers exactly
          what the tour has taken over: the cells. The docks, the top bar and END TURN are outside
          it and the map's own strip sits above it, because the round is still the player's to
          end while they are being shown something. */}
      <button type="button" className="spawn-tour-catch" onClick={skip} aria-label={t("game.spawnTour.skip")} />
      <div
        className="round-flash spawn-tour"
        role="status"
        aria-live="polite"
        /* `key` on the arrival: the banner is one element re-entering per newcomer, so each gets
           the arrival animation rather than the second appearing inside the first one's hold. */
        key={arrival.key}
      >
        <span className="round-flash-label spawn-tour-title">{t("game.spawnTour.title")}</span>
        <span className="spawn-tour-line">{line}</span>
        <span className="spawn-tour-hint">{t("game.spawnTour.skip")}</span>
      </div>
    </>
  )
}

export default SpawnTour
